import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { API_ENDPOINTS } from '../config/constants';
import './CRMListConfigModal.css';
import AppIcon from './icons/AppIcon';

export default function CRMListConfigModal({ list, onClose, onSave, onDelete, projectId }) {
  const isNewList = !list;
  const [loading, setLoading] = useState(false);
  const [projectType, setProjectType] = useState(null);
  const [images, setImages] = useState([]);  // ✅ Iniciar vazio, carregar do banco
  const [connection, setConnection] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [toolEnabled, setToolEnabled] = useState(false);  // ✅ Iniciar falso, carregar do banco
  const fileInputRef = useRef(null);  // ✅ Ref para resetar input file
  const hasLoadedRef = useRef(false);  // ✅ Flag para carregar dados apenas uma vez
  const MAX_IMAGES = 3;
  const MAX_IMAGE_SIZE = 3 * 1024 * 1024;  // 3MB

  const [initialAudioEnabled, setInitialAudioEnabled] = useState(false);
  const [initialAudioUrl, setInitialAudioUrl] = useState('');
  const [audioPreviewUrl, setAudioPreviewUrl] = useState('');
  const [blockAgentResponseForInitialAudio, setBlockAgentResponseForInitialAudio] = useState(true);
  const [audioRecording, setAudioRecording] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioObjectUrlRef = useRef(null);

  // Follow-up Rules States
  const [followUpRules, setFollowUpRules] = useState([]);
  const [projectLists, setProjectLists] = useState([]);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [newRule, setNewRule] = useState({ targetListId: '', delayHours: 24 });
  const [loadingRules, setLoadingRules] = useState(false);
  const [followUpEnabled, setFollowUpEnabled] = useState(false);  // ✅ Toggle para habilitar/desabilitar follow-up

  // ✅ NOVO: Mensagem Inicial Fixa States
  const [initialMessageEnabled, setInitialMessageEnabled] = useState(false);
  const [initialMessage, setInitialMessage] = useState('');
  const [blockAgentResponse, setBlockAgentResponse] = useState(true);  // Por padrão, bloqueia agente quando msg fixa

  // ✅ NOVO: Botão de Link States
  const [buttonLinkEnabled, setButtonLinkEnabled] = useState(false);
  const [buttonLinks, setButtonLinks] = useState([]);  // Array de botões {label, url}
  const [newButtonLabel, setNewButtonLabel] = useState('');
  const [newButtonUrl, setNewButtonUrl] = useState('');
  const [buttonLinkMessage, setButtonLinkMessage] = useState('Clique em um dos botões abaixo:');
  const [blockAgentResponseForButtonLink, setBlockAgentResponseForButtonLink] = useState(false);
  const MAX_BUTTONS = 4;

  // ✅ NOVO: Distribuição Round-Robin States
  const [distributionEnabled, setDistributionEnabled] = useState(false);
  const [distributionTargetLists, setDistributionTargetLists] = useState([]);
  const [distributionConfig, setDistributionConfig] = useState(null);
  const [loadingDistribution, setLoadingDistribution] = useState(false);

  // ✅ NOVO: Encaminhar para Projeto States
  const [forwardEnabled, setForwardEnabled] = useState(false);
  const [forwardTargetProjectId, setForwardTargetProjectId] = useState('');
  const [forwardTargetListId, setForwardTargetListId] = useState('');
  const [forwardMode, setForwardMode] = useState('immediate'); // 'immediate' | 'scheduled'
  const [forwardDelayHours, setForwardDelayHours] = useState(1);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableLists, setAvailableLists] = useState([]);
  const [forwardConfig, setForwardConfig] = useState(null);
  const [loadingForward, setLoadingForward] = useState(false);
  const [disableAiWhileInList, setDisableAiWhileInList] = useState(false);
  const [moveToAtendimentoAfter5Messages, setMoveToAtendimentoAfter5Messages] = useState(true);

  // ✅ Carregar dados atualizados da lista do banco de dados (apenas uma vez)
  useEffect(() => {
    const loadListData = async () => {
      if (!list?.id || hasLoadedRef.current) return;
      
      hasLoadedRef.current = true;
      
      try {
        const { data: updatedList, error } = await supabase
          .from('project_lists')
          .select('metadata, automation_rules')
          .eq('id', list.id)
          .single();
        
        if (error) {
          console.error('Erro ao carregar lista:', error);
          return;
        }
        
        // Atualizar estados com dados do banco
        const loadedImages = [];
        if (updatedList?.metadata?.initial_images && Array.isArray(updatedList.metadata.initial_images)) {
          loadedImages.push(...updatedList.metadata.initial_images);
        } else if (updatedList?.metadata?.initial_image) {
          // Compatibilidade com imagem única (migração)
          loadedImages.push(updatedList.metadata.initial_image);
        }
        
        setImages(loadedImages);
        
        if (updatedList?.metadata?.enabled !== undefined) {
          setToolEnabled(updatedList.metadata.enabled);
        }

        if (updatedList?.metadata?.initial_audio_enabled !== undefined) {
          setInitialAudioEnabled(!!updatedList.metadata.initial_audio_enabled);
        }
        if (updatedList?.metadata?.initial_audio_url) {
          setInitialAudioUrl(updatedList.metadata.initial_audio_url);
        }
        if (updatedList?.metadata?.initial_audio_block_agent_response !== undefined) {
          setBlockAgentResponseForInitialAudio(updatedList.metadata.initial_audio_block_agent_response !== false);
        }
        if (updatedList?.metadata?.disable_ai_while_in_list !== undefined) {
          setDisableAiWhileInList(!!updatedList.metadata.disable_ai_while_in_list);
        }
        if (updatedList?.metadata?.auto_move_to_em_atendimento_after_5_messages !== undefined) {
          setMoveToAtendimentoAfter5Messages(!!updatedList.metadata.auto_move_to_em_atendimento_after_5_messages);
        }

        // ✅ NOVO: Carregar configurações de mensagem inicial fixa
        const automationRules = updatedList?.automation_rules || {};
        setInitialMessageEnabled(automationRules.initial_message_enabled || false);
        setInitialMessage(automationRules.initial_message || '');
        setBlockAgentResponse(automationRules.block_agent_response !== false);  // Default true

        // ✅ NOVO: Carregar configurações de botão de link
        if (updatedList?.metadata?.button_link_enabled !== undefined) {
          setButtonLinkEnabled(!!updatedList.metadata.button_link_enabled);
        }
        if (updatedList?.metadata?.button_links && Array.isArray(updatedList.metadata.button_links)) {
          setButtonLinks(updatedList.metadata.button_links);
        }
        if (updatedList?.metadata?.button_link_message) {
          setButtonLinkMessage(updatedList.metadata.button_link_message);
        }
        if (updatedList?.metadata?.block_agent_response_for_button_link !== undefined) {
          setBlockAgentResponseForButtonLink(!!updatedList.metadata.block_agent_response_for_button_link);
        }

        // ✅ NOVO: Carregar configuração de distribuição
        await loadDistributionConfig();
        // ✅ NOVO: Carregar configuração de encaminhamento
        await loadForwardConfig();
      } catch (error) {
        console.error('Erro ao carregar dados da lista:', error);
      }
    };

    const loadForwardConfig = async () => {
      if (!list?.id || !projectId) return;
      try {
        setLoadingForward(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;

        // Carregar configuração de encaminhamento
        const response = await fetch(
          `${API_ENDPOINTS.LEAD_FORWARD}/lists/${list.id}/forward`,
          {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          }
        );

        if (response.ok) {
          const { data } = await response.json();
          if (data) {
            setForwardEnabled(data.is_enabled || false);
            setForwardTargetProjectId(data.target_project_id || '');
            setForwardTargetListId(data.target_list_id || '');
            setForwardMode(data.delay_hours ? 'scheduled' : 'immediate');
            setForwardDelayHours(data.delay_hours || 1);
            setForwardConfig(data);
          }
        }

        // Carregar projetos disponíveis
        const projectsResponse = await fetch(
          `${API_ENDPOINTS.LEAD_FORWARD}/projects`,
          {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          }
        );

        if (projectsResponse.ok) {
          const { data: projects } = await projectsResponse.json();
          setAvailableProjects(projects || []);
        }
      } catch (error) {
        console.error('Erro ao carregar configuração de encaminhamento:', error);
      } finally {
        setLoadingForward(false);
      }
    };

    const loadDistributionConfig = async () => {
      if (!list?.id || !projectId) return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;

        const response = await fetch(
          `${API_ENDPOINTS.LEAD_DISTRIBUTION}/lists/${list.id}/distribution?project_id=${projectId}`,
          {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          }
        );

        if (response.ok) {
          const { data } = await response.json();
          if (data) {
            setDistributionEnabled(data.is_enabled || false);
            setDistributionTargetLists(data.target_lists || []);
            setDistributionConfig(data);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar configuração de distribuição:', error);
      }
    };
    
    loadListData();
  }, [list?.id]);

  useEffect(() => {
    return () => {
      try {
        if (audioObjectUrlRef.current) {
          URL.revokeObjectURL(audioObjectUrlRef.current);
          audioObjectUrlRef.current = null;
        }
      } catch (_) {}
    };
  }, []);

  const getAudioMimeType = () => {
    try {
      const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg'
      ];
      for (const c of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) {
          return c;
        }
      }
    } catch (_) {}
    return '';
  };

  const startAudioRecording = async () => {
    try {
      if (audioRecording) return;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = getAudioMimeType();
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mr.onstop = async () => {
        try {
          stream.getTracks().forEach(t => t.stop());
        } catch (_) {}

        const chunks = audioChunksRef.current || [];
        audioChunksRef.current = [];
        if (chunks.length === 0) return;

        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        if (blob.size <= 0) return;

        try {
          if (audioObjectUrlRef.current) {
            URL.revokeObjectURL(audioObjectUrlRef.current);
            audioObjectUrlRef.current = null;
          }
          audioObjectUrlRef.current = URL.createObjectURL(blob);
          setAudioPreviewUrl(audioObjectUrlRef.current);
        } catch (_) {}

        await uploadRecordedAudio(blob);
      };

      mediaRecorderRef.current = mr;
      mr.start();
      setAudioRecording(true);
    } catch (error) {
      console.error('Erro ao iniciar gravação de áudio:', error);
      alert('Erro ao acessar microfone. Verifique as permissões do navegador.');
      setAudioRecording(false);
    }
  };

  const stopAudioRecording = async () => {
    try {
      const mr = mediaRecorderRef.current;
      if (!mr) return;
      if (mr.state === 'inactive') return;
      mr.stop();
      setAudioRecording(false);
      mediaRecorderRef.current = null;
    } catch (error) {
      console.error('Erro ao parar gravação de áudio:', error);
      setAudioRecording(false);
    }
  };

  const parseStorageRefFromPublicUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      const m = url.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
      if (m) return { bucket: m[1], path: m[2] };
      return null;
    } catch (_) {
      return null;
    }
  };

  const uploadRecordedAudio = async (blob) => {
    try {
      setAudioUploading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      if (!user?.company_id) throw new Error('Usuário sem company_id');

      const MAX_AUDIO_SIZE = 10 * 1024 * 1024;
      if (blob.size > MAX_AUDIO_SIZE) {
        throw new Error('Áudio muito grande. Limite: 10MB');
      }

      try {
        if (initialAudioUrl) {
          const ref = parseStorageRefFromPublicUrl(initialAudioUrl);
          if (ref) {
            await supabase.storage.from(ref.bucket).remove([ref.path]);
          }
        }
      } catch (_) {}

      const bucket = 'project-attachments';
      const ext = (blob.type || '').includes('ogg') ? 'ogg' : 'webm';
      const fileName = `crm-initial-audio/${user.company_id}/${projectId}/${list?.id || 'unknown'}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, blob, {
          cacheControl: '3600',
          upsert: true,
          contentType: blob.type || 'audio/webm'
        });

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      const publicUrl = urlData?.publicUrl || '';
      if (!publicUrl) throw new Error('Falha ao obter URL pública do áudio');
      setInitialAudioUrl(publicUrl);
      setInitialAudioEnabled(true);
      setAudioPreviewUrl(publicUrl);
    } catch (error) {
      console.error('Erro ao fazer upload do áudio:', error);
      alert('Erro ao salvar áudio: ' + error.message);
    } finally {
      setAudioUploading(false);
    }
  };

  const handleRemoveAudio = async () => {
    try {
      if (!initialAudioUrl) {
        setInitialAudioEnabled(false);
        return;
      }
      const ref = parseStorageRefFromPublicUrl(initialAudioUrl);
      if (ref) {
        await supabase.storage.from(ref.bucket).remove([ref.path]);
      }
      setInitialAudioUrl('');
      setInitialAudioEnabled(false);
      setAudioPreviewUrl('');
    } catch (error) {
      console.error('Erro ao remover áudio:', error);
      alert('Erro ao remover áudio: ' + error.message);
    }
  };

  // ✅ Carregar conexão do projeto
  useEffect(() => {
    const loadConnection = async () => {
      try {
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('settings, project_type')
          .eq('id', projectId)
          .single();

        if (projectError) throw projectError;
        setProjectType(project?.project_type || null);

        // Buscar connection_id de crm ou crm_multi
        let connectionId = null;
        if (project?.project_type === 'crm_multi' || project?.project_type === 'marketing_campaign') {
          const connectionIds = project?.settings?.crm_multi?.connection_ids || [];
          if (project?.project_type === 'marketing_campaign') {
            const marketingConnectionIds = project?.settings?.marketing_campaign?.connection_ids || [];
            connectionId = marketingConnectionIds[0] || null;
          } else {
            connectionId = connectionIds[0] || null; // Usa a primeira conexão
          }
        } else {
          connectionId = project?.settings?.crm?.connection_id;
        }

        if (connectionId) {
          const { data: conn, error: connError } = await supabase
            .from('connections')
            .select('id, name, number')
            .eq('id', connectionId)
            .single();

          if (connError) throw connError;
          setConnection(conn);
        }
      } catch (error) {
        console.error('Erro ao carregar conexão:', error);
      }
    };

    if (projectId) {
      loadConnection();
    }
  }, [projectId]);

  // ✅ Carregar listas do projeto para o dropdown de destino
  useEffect(() => {
    const loadProjectLists = async () => {
      try {
        const { data: lists, error } = await supabase
          .from('project_lists')
          .select('id, name')
          .eq('project_id', projectId)
          .order('name', { ascending: true });

        if (error) throw error;
        setProjectLists(lists || []);
      } catch (error) {
        console.error('Erro ao carregar listas do projeto:', error);
      }
    };

    if (projectId) {
      loadProjectLists();
    }
  }, [projectId]);

  // ✅ Carregar regras de follow-up da lista
  useEffect(() => {
    const loadFollowUpRules = async () => {
      if (!list?.id) return;
      
      try {
        setLoadingRules(true);
        const { data: rules, error } = await supabase
          .from('crm_followup_rules')
          .select('*')
          .eq('origin_list_id', list.id)
          .order('order_index', { ascending: true });

        if (error) throw error;
        
        setFollowUpRules(rules || []);
        
        // ✅ Atualizar estado de habilitação com base nas regras
        // Se há regras ativas, o follow-up está habilitado
        const hasActiveRules = rules && rules.some(r => r.active);
        setFollowUpEnabled(hasActiveRules || false);
        
        console.log('✅ Regras carregadas:', {
          total: rules?.length || 0,
          ativas: rules?.filter(r => r.active).length || 0,
          followUpEnabled: hasActiveRules
        });
      } catch (error) {
        console.error('Erro ao carregar regras de follow-up:', error);
      } finally {
        setLoadingRules(false);
      }
    };

    loadFollowUpRules();
  }, [list?.id]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validar quantidade de imagens
    if (images.length + files.length > MAX_IMAGES) {
      alert(`Máximo de ${MAX_IMAGES} imagens permitidas. Você já tem ${images.length} imagem(ns).`);
      return;
    }

    // Processar cada arquivo
    const newImages = [];
    for (const file of files) {
      // Validar tamanho (3MB)
      if (file.size > MAX_IMAGE_SIZE) {
        alert(`A imagem "${file.name}" deve ter no máximo 3MB`);
        continue;
      }

      // Validar tipo
      if (!file.type.startsWith('image/')) {
        alert(`"${file.name}" não é uma imagem válida`);
        continue;
      }

      // Converter para base64
      await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newImages.push(reader.result);
          resolve();
        };
        reader.onerror = () => {
          alert(`Erro ao processar "${file.name}"`);
          reject();
        };
        reader.readAsDataURL(file);
      });
    }

    // Adicionar novas imagens ao array
    if (newImages.length > 0) {
      setImages(prevImages => [...prevImages, ...newImages]);
    }

    // Limpar input file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // ✅ Salvar configuração de distribuição
      if (distributionEnabled && distributionTargetLists.length > 0) {
        await saveDistributionConfig();
      }

      // ✅ Salvar configuração de encaminhamento
      if (forwardEnabled && forwardTargetProjectId && forwardTargetListId) {
        await saveForwardConfig();
      }

      // ✅ Preparar dados da lista com múltiplas imagens
      const listData = {
        ...list,
        metadata: {
          ...list?.metadata,
          initial_images: images,  // ✅ Salvar array de imagens
          enabled: toolEnabled,  // ✅ Salvar estado de habilitação
          initial_audio_enabled: initialAudioEnabled,
          initial_audio_url: initialAudioUrl || null,
          initial_audio_block_agent_response: blockAgentResponseForInitialAudio,
          button_link_enabled: buttonLinkEnabled,  // ✅ Salvar estado de botões
          button_links: buttonLinks,  // ✅ Salvar array de botões
          button_link_message: buttonLinkMessage,  // ✅ Salvar mensagem dos botões
          block_agent_response_for_button_link: blockAgentResponseForButtonLink,  // ✅ Bloquear agente quando botão ativo
          // Regra exclusiva de marketing_campaign na lista Leads Recebidos
          disable_ai_while_in_list: projectType === 'marketing_campaign' ? disableAiWhileInList : false,
          // Regra de encaminhamento automático por volume de mensagens
          auto_move_to_em_atendimento_after_5_messages: moveToAtendimentoAfter5Messages
        }
      };

      // ✅ NOVO: Preparar automation_rules com mensagem inicial fixa
      const automationRules = {
        initial_message_enabled: initialMessageEnabled,
        initial_message: initialMessage,
        block_agent_response: blockAgentResponse
      };

      // Salvar no Supabase
      const { error } = await supabase
        .from('project_lists')
        .update({
          metadata: listData.metadata,
          automation_rules: automationRules  // ✅ Salvar configurações de mensagem inicial
        })
        .eq('id', list.id)
        .eq('project_id', projectId);

      if (error) throw error;

      alert('Configurações salvas com sucesso!');
      onSave(listData);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // ✅ NOVO: Adicionar botão de link
  const handleAddButton = () => {
    if (!newButtonLabel.trim() || !newButtonUrl.trim()) {
      alert('Por favor, preencha o rótulo e a URL do botão');
      return;
    }

    try {
      new URL(newButtonUrl);
    } catch (e) {
      alert('URL inválida. Por favor, insira uma URL válida (ex: https://exemplo.com)');
      return;
    }

    if (buttonLinks.length >= MAX_BUTTONS) {
      alert(`Máximo de ${MAX_BUTTONS} botões permitidos`);
      return;
    }

    setButtonLinks([...buttonLinks, { label: newButtonLabel, url: newButtonUrl }]);
    setNewButtonLabel('');
    setNewButtonUrl('');
  };

  // ✅ NOVO: Remover botão de link
  const handleRemoveButton = (index) => {
    setButtonLinks(buttonLinks.filter((_, i) => i !== index));
  };

  // ✅ Criar nova regra de follow-up
  const handleCreateFollowUpRule = async () => {
    try {
      if (!newRule.targetListId || !newRule.delayHours) {
        alert('Por favor, preencha todos os campos');
        return;
      }

      const targetList = projectLists.find(l => l.id === newRule.targetListId);
      if (!targetList) {
        alert('Lista de destino não encontrada');
        return;
      }

      setLoadingRules(true);

      // Obter token de forma correta
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sessão expirada. Por favor, faça login novamente');
      }

      console.log('🔑 Token obtido:', {
        accessToken: session.access_token.substring(0, 30) + '...',
        user: session.user?.email,
        expiresAt: session.expires_at
      });

      const url = API_ENDPOINTS.CRM_FOLLOWUP_RULES;
      console.log('📡 Enviando POST para:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          projectId,
          originListId: list.id,
          originListName: list.title,
          targetListId: newRule.targetListId,
          targetListName: targetList.name,
          delayHours: parseInt(newRule.delayHours)
        })
      });

      console.log('📨 Resposta recebida:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      let responseData;
      try {
        responseData = await response.json();
        console.log('📦 Dados da resposta:', responseData);
      } catch (parseError) {
        console.error('❌ Erro ao fazer parse da resposta:', parseError);
        console.error('❌ Status:', response.status);
        console.error('❌ Status Text:', response.statusText);
        console.error('❌ Content-Type:', response.headers.get('content-type'));
        throw new Error(`Erro ${response.status} (${response.statusText}): Resposta inválida do servidor. Verifique se o servidor está rodando.`);
      }

      if (!response.ok) {
        console.error('❌ Resposta não OK:', responseData);
        throw new Error(responseData?.error || `Erro ${response.status}: ${responseData?.message || 'Erro ao criar regra'}`);
      }

      const rule = responseData.data;
      setFollowUpRules([...followUpRules, rule]);
      setNewRule({ targetListId: '', delayHours: 24 });
      setShowFollowUpForm(false);
      alert('Regra de follow-up criada com sucesso!');
    } catch (error) {
      console.error('Erro ao criar regra:', error);
      alert('Erro ao criar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // ✅ Deletar regra de follow-up
  const handleDeleteFollowUpRule = async (ruleId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta regra?')) return;

    try {
      setLoadingRules(true);

      const response = await fetch(`${API_ENDPOINTS.CRM_FOLLOWUP_RULES}/${ruleId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ projectId })
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar regra');
      }

      setFollowUpRules(followUpRules.filter(r => r.id !== ruleId));
      alert('Regra deletada com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar regra:', error);
      alert('Erro ao deletar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // ✅ Ativar/Desativar regra de follow-up
  const handleToggleFollowUpRule = async (rule) => {
    try {
      setLoadingRules(true);

      const response = await fetch(`${API_ENDPOINTS.CRM_FOLLOWUP_RULES}/${rule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          projectId,
          active: !rule.active
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar regra');
      }

      const { data: updatedRule } = await response.json();
      setFollowUpRules(followUpRules.map(r => r.id === rule.id ? updatedRule : r));
    } catch (error) {
      console.error('Erro ao atualizar regra:', error);
      alert('Erro ao atualizar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // ✅ NOVO: Salvar configuração de distribuição
  const saveDistributionConfig = async () => {
    try {
      setLoadingDistribution(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Sessão expirada');

      const response = await fetch(
        `${API_ENDPOINTS.LEAD_DISTRIBUTION}/lists/${list.id}/distribution`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            project_id: projectId,
            is_enabled: distributionEnabled,
            target_lists: distributionTargetLists
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao salvar configuração');
      }

      const { data } = await response.json();
      setDistributionConfig(data);
      return data;
    } catch (error) {
      console.error('Erro ao salvar distribuição:', error);
      alert('Erro ao salvar configuração de distribuição: ' + error.message);
      throw error;
    } finally {
      setLoadingDistribution(false);
    }
  };

  // ✅ NOVO: Adicionar lista de distribuição
  const handleAddDistributionList = (listId) => {
    const targetList = projectLists.find(l => l.id === listId);
    if (!targetList) return;
    if (distributionTargetLists.find(t => t.list_id === listId)) return;

    setDistributionTargetLists([...distributionTargetLists, {
      list_id: targetList.id,
      name: targetList.name,
      order: distributionTargetLists.length
    }]);
  };

  // ✅ NOVO: Remover lista de distribuição
  const handleRemoveDistributionList = (index) => {
    const newLists = distributionTargetLists.filter((_, i) => i !== index);
    setDistributionTargetLists(newLists.map((item, i) => ({ ...item, order: i })));
  };

  // ✅ NOVO: Mover lista na ordem
  const handleMoveDistributionList = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === distributionTargetLists.length - 1) return;

    const newLists = [...distributionTargetLists];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newLists[index], newLists[swapIndex]] = [newLists[swapIndex], newLists[index]];
    setDistributionTargetLists(newLists.map((item, i) => ({ ...item, order: i })));
  };

  // ✅ NOVO: Carregar listas do projeto selecionado para encaminhamento
  const handleProjectChange = async (projectId) => {
    setForwardTargetProjectId(projectId);
    setForwardTargetListId('');
    
    if (!projectId) {
      setAvailableLists([]);
      return;
    }
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(
        `${API_ENDPOINTS.LEAD_FORWARD}/projects/${projectId}/lists`,
        {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        }
      );

      if (response.ok) {
        const { data } = await response.json();
        setAvailableLists(data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar listas do projeto:', error);
    }
  };

  // ✅ NOVO: Salvar configuração de encaminhamento
  const saveForwardConfig = async () => {
    try {
      setLoadingForward(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Sessão expirada');

      const response = await fetch(
        `${API_ENDPOINTS.LEAD_FORWARD}/lists/${list.id}/forward`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            project_id: projectId,
            is_enabled: forwardEnabled,
            target_project_id: forwardTargetProjectId,
            target_list_id: forwardTargetListId,
            delay_hours: forwardMode === 'scheduled' ? forwardDelayHours : null
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao salvar configuração');
      }

      const { data } = await response.json();
      setForwardConfig(data);
      return data;
    } catch (error) {
      console.error('Erro ao salvar encaminhamento:', error);
      alert('Erro ao salvar configuração de encaminhamento: ' + error.message);
      throw error;
    } finally {
      setLoadingForward(false);
    }
  };

  return (
    <div className="modal-overlay-crm-list" onClick={onClose}>
      <div className="crm-list-config-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="crm-modal-header">
          <div className="crm-modal-title-section">
            <AppIcon name="settings" />
            <div>
              <h2>Configurar</h2>
              <p className="list-name">{list?.title}</p>
            </div>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="crm-modal-content">
          {/* Seção Principal */}
          <div className="crm-modal-main">
            {/* ✅ Seção de Habilitação da Ferramenta */}
            <div className="crm-section">
              <h4>
                <AppIcon name="power_settings_new" />
                Habilitar envio de imagem
              </h4>
              
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={toolEnabled}
                    onChange={(e) => setToolEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {toolEnabled 
                      ? '✅ Ferramenta ativa' 
                      : '❌ Ferramenta inativa'}
                  </span>
                </label>
                <small className="toggle-description">
                  {toolEnabled 
                    ? 'Imagem será enviada para todos os novos leads recebidos nesta lista'
                    : 'Nenhuma imagem será enviada para novos leads'}
                </small>
              </div>
            </div>

            {/* Upload de Imagens */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="image" />
                  <h4>Enviar Imagem Inicial ({images.length}/{MAX_IMAGES})</h4>
                </div>
                {/* ✅ Ícone de informação com tooltip */}
                <div className="tooltip-container">
                  <button
                    className="info-icon-btn"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    type="button"
                  >
                    <AppIcon name="info" />
                  </button>
                  {showTooltip && (
                    <div className="tooltip">
                      Quando habilitada, esta ferramenta irá enviar as imagens para todo lead recebido nesta lista. 
                      Você pode adicionar até 3 imagens que serão enviadas em sequência. 
                      Se estiver configurada com algum agente de IA, o agente irá continuar enviando texto normalmente.
                    </div>
                  )}
                </div>
              </div>

              <div className="image-upload-container">
                <div className="upload-area">
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="image-input"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={images.length >= MAX_IMAGES}
                    style={{ display: 'none' }}
                  />
                  <label 
                    htmlFor="image-input" 
                    className="upload-label"
                    style={{ opacity: images.length >= MAX_IMAGES ? 0.5 : 1, cursor: images.length >= MAX_IMAGES ? 'not-allowed' : 'pointer' }}
                  >
                    <AppIcon name="cloud_upload" />
                    <span>{images.length >= MAX_IMAGES ? 'Limite de imagens atingido' : 'Clique para selecionar imagens'}</span>
                    <small>ou arraste e solte aqui</small>
                  </label>
                </div>

                {/* Previews das Imagens */}
                {images.length > 0 && (
                  <div className="images-preview-container">
                    <div className="preview-label">Previews ({images.length}):</div>
                    <div className="images-grid">
                      {images.map((image, index) => (
                        <div key={index} className="image-preview-item">
                          <img src={image} alt={`preview-${index}`} />
                          <div className="image-number">{index + 1}</div>
                          <button
                            className="btn-remove-image-small"
                            onClick={() => handleRemoveImage(index)}
                            type="button"
                            title="Remover imagem"
                          >
                            <AppIcon name="delete" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="image-info">
                <p>
                  <strong>Formatos suportados:</strong> JPG, PNG, GIF, WebP
                </p>
                <p>
                  <strong>Tamanho máximo por imagem:</strong> 3MB
                </p>
                <p>
                  <strong>Máximo de imagens:</strong> {MAX_IMAGES}
                </p>
                <p>
                  <strong>Recomendação:</strong> Use imagens com proporção 1:1 ou 16:9 para melhor visualização
                </p>
              </div>
            </div>

            {/* ✅ NOVO: Seção de Mensagem Inicial Fixa */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="chat" />
                  <h4>Mensagem Inicial Fixa</h4>
                </div>
              </div>

              {/* Toggle de Habilitação */}
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={initialMessageEnabled}
                    onChange={(e) => setInitialMessageEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {initialMessageEnabled 
                      ? '✅ Mensagem inicial ativa' 
                      : '❌ Mensagem inicial inativa'}
                  </span>
                </label>
                <small className="toggle-description">
                  {initialMessageEnabled 
                    ? 'Uma mensagem fixa será enviada quando um lead entrar nesta lista'
                    : 'Nenhuma mensagem fixa será enviada'}
                </small>
              </div>

              {/* Configuração da Mensagem */}
              {initialMessageEnabled && (
                <div className="initial-message-config">
                  <div className="form-group">
                    <label>Mensagem a ser enviada:</label>
                    <textarea
                      value={initialMessage}
                      onChange={(e) => setInitialMessage(e.target.value)}
                      placeholder="Digite a mensagem que será enviada automaticamente quando um lead entrar nesta lista...&#10;&#10;Exemplo: Olá! Obrigado pelo seu contato. Em breve um de nossos atendentes irá te responder."
                      rows={4}
                      className="initial-message-textarea"
                    />
                  </div>

                  {/* Preview da Mensagem */}
                  {initialMessage && (
                    <div className="message-preview">
                      <label>Preview:</label>
                      <div className="preview-box">
                        {initialMessage}
                      </div>
                    </div>
                  )}

                  {/* Toggle Bloquear Agente */}
                  <div className="block-agent-section">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={blockAgentResponse}
                        onChange={(e) => setBlockAgentResponse(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-text">
                        Bloquear resposta do agente IA
                      </span>
                    </label>
                    <small className="toggle-description">
                      {blockAgentResponse 
                        ? '⚠️ O agente NÃO responderá quando esta mensagem for enviada. A mensagem será registrada no histórico para contexto futuro.'
                        : '✅ O agente responderá normalmente após esta mensagem ser enviada.'}
                    </small>
                  </div>

                  <div className="info-box">
                    <AppIcon name="info" />
                    <p>
                      A mensagem será registrada no histórico da conversa para que o agente tenha contexto quando o cliente responder posteriormente.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {projectType === 'marketing_campaign' && (
              <div className="crm-section">
                <div className="section-header">
                  <div className="header-left">
                    <AppIcon name="smart_toy" />
                    <h4>Desativar Agente de IA nesta lista</h4>
                  </div>
                </div>

                <div className="tool-enable-section">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={disableAiWhileInList}
                      onChange={(e) => setDisableAiWhileInList(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      {disableAiWhileInList
                        ? '✅ Agente desativado enquanto estiver em Leads Recebidos'
                        : '❌ Agente ativo normalmente nesta lista'}
                    </span>
                  </label>
                  <small className="toggle-description">
                    {disableAiWhileInList
                      ? 'Enquanto o card estiver em "Leads Recebidos", o agente de IA não responderá.'
                      : 'O agente poderá responder normalmente mesmo com o card em "Leads Recebidos".'}
                  </small>
                </div>
              </div>
            )}

            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="swap_horiz" />
                  <h4>Encaminhamento automático por mensagens</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={moveToAtendimentoAfter5Messages}
                    onChange={(e) => setMoveToAtendimentoAfter5Messages(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    Encaminhar para em atendimento depois de 5 mensagens
                  </span>
                </label>
                <small className="toggle-description">
                  {moveToAtendimentoAfter5Messages
                    ? 'Quando o lead atingir 5+ mensagens, o card sai de "Leads Recebidos" e vai para "Em Atendimento".'
                    : 'Desativado: o card permanece em "Leads Recebidos" mesmo após 5+ mensagens.'}
                </small>
              </div>
            </div>

            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="mic" />
                  <h4>Enviar áudio inicial</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={initialAudioEnabled}
                    onChange={(e) => {
                      const next = e.target.checked;
                      if (next && !initialAudioUrl) {
                        alert('Grave e salve um áudio antes de habilitar.');
                        return;
                      }
                      setInitialAudioEnabled(next);
                    }}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {initialAudioEnabled ? '✅ Áudio ativo' : '❌ Áudio inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {initialAudioUrl
                    ? 'Áudio será enviado para novos leads recebidos nesta lista'
                    : 'Grave um áudio para habilitar esta ferramenta'}
                </small>
              </div>

              <div className="initial-message-config">
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-add-followup-rule"
                      onClick={startAudioRecording}
                      disabled={audioUploading || audioRecording}
                    >
                      {audioRecording ? 'Gravando...' : 'Gravar áudio'}
                    </button>

                    <button
                      type="button"
                      className="btn-cancel-form"
                      onClick={stopAudioRecording}
                      disabled={audioUploading || !audioRecording}
                    >
                      Parar
                    </button>

                    <button
                      type="button"
                      className="btn-delete-rule"
                      onClick={handleRemoveAudio}
                      disabled={audioUploading || (!initialAudioUrl && audioRecording)}
                    >
                      Remover áudio
                    </button>
                  </div>
                </div>

                {audioUploading && (
                  <small className="toggle-description">Salvando áudio...</small>
                )}

                {(initialAudioUrl || audioPreviewUrl) && (
                  <div className="message-preview">
                    <label>Preview:</label>
                    <div className="preview-box">
                      <audio
                        controls
                        src={audioPreviewUrl || initialAudioUrl}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                )}

                <div className="block-agent-section">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={blockAgentResponseForInitialAudio}
                      onChange={(e) => setBlockAgentResponseForInitialAudio(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      Bloquear resposta do agente IA
                    </span>
                  </label>
                  <small className="toggle-description">
                    {blockAgentResponseForInitialAudio
                      ? '⚠️ O agente NÃO responderá quando este áudio for enviado.'
                      : '✅ O agente responderá normalmente após o áudio ser enviado.'}
                  </small>
                </div>
              </div>
            </div>

            {/* ✅ NOVO: Seção de Botão de Link */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="link" />
                  <h4>Enviar Botões de Link</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={buttonLinkEnabled}
                    onChange={(e) => setButtonLinkEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {buttonLinkEnabled ? '✅ Botões ativos' : '❌ Botões inativos'}
                  </span>
                </label>
                <small className="toggle-description">
                  {buttonLinkEnabled 
                    ? 'Botões de link serão enviados para novos leads recebidos nesta lista'
                    : 'Nenhum botão será enviado para novos leads'}
                </small>
              </div>

              {buttonLinkEnabled && (
                <div className="initial-message-config">
                  <div className="form-group">
                    <label>Mensagem antes dos botões:</label>
                    <textarea
                      value={buttonLinkMessage}
                      onChange={(e) => setButtonLinkMessage(e.target.value)}
                      placeholder="Digite a mensagem que será enviada antes dos botões..."
                      rows={2}
                      className="initial-message-textarea"
                    />
                  </div>

                  <div className="button-form">
                    <div className="form-group">
                      <label>Rótulo do botão:</label>
                      <input
                        type="text"
                        value={newButtonLabel}
                        onChange={(e) => setNewButtonLabel(e.target.value)}
                        placeholder="Ex: Visitar Site"
                        maxLength={20}
                      />
                    </div>

                    <div className="form-group">
                      <label>URL do botão:</label>
                      <input
                        type="url"
                        value={newButtonUrl}
                        onChange={(e) => setNewButtonUrl(e.target.value)}
                        placeholder="Ex: https://exemplo.com"
                      />
                    </div>

                    <button
                      type="button"
                      className="btn-add-followup-rule"
                      onClick={handleAddButton}
                      disabled={buttonLinks.length >= MAX_BUTTONS}
                    >
                      <AppIcon name="add" />
                      Adicionar Botão ({buttonLinks.length}/{MAX_BUTTONS})
                    </button>
                  </div>

                  {buttonLinks.length > 0 && (
                    <div className="buttons-list">
                      <div className="buttons-label">Botões Configurados ({buttonLinks.length}):</div>
                      <div className="buttons-container">
                        {buttonLinks.map((button, index) => (
                          <div key={index} className="button-item">
                            <div className="button-info">
                              <div className="button-label">{button.label}</div>
                              <div className="button-url">{button.url}</div>
                            </div>
                            <button
                              className="btn-delete-rule"
                              onClick={() => handleRemoveButton(index)}
                              type="button"
                              title="Remover botão"
                            >
                              <AppIcon name="delete" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {buttonLinks.length > 0 && (
                    <div className="message-preview">
                      <label>Preview:</label>
                      <div className="preview-box">
                        <div style={{ marginBottom: '0.5rem', color: '#cbd5e0' }}>
                          {buttonLinkMessage}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {buttonLinks.map((button, index) => (
                            <button
                              key={index}
                              style={{
                                padding: '0.75rem',
                                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                border: '1px solid rgba(59, 130, 246, 0.5)',
                                borderRadius: '8px',
                                color: '#3b82f6',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500'
                              }}
                              disabled
                            >
                              {button.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ✅ NOVO: Opção de bloquear resposta do agente IA */}
                  <div className="tool-enable-section">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={blockAgentResponseForButtonLink}
                        onChange={(e) => setBlockAgentResponseForButtonLink(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-text">
                        {blockAgentResponseForButtonLink 
                          ? '✅ Bloquear resposta do agente IA' 
                          : '❌ Permitir resposta do agente IA'}
                      </span>
                    </label>
                    <small className="toggle-description">
                      {blockAgentResponseForButtonLink 
                        ? 'O agente de IA não responderá quando os botões forem enviados, evitando duplicação de mensagens'
                        : 'O agente de IA poderá responder normalmente quando os botões forem enviados'}
                    </small>
                  </div>

                  <div className="info-box">
                    <AppIcon name="info" />
                    <p>
                      Os botões serão enviados via WhatsApp como menu interativo. Máximo de {MAX_BUTTONS} botões por mensagem.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* ✅ NOVO: Seção de Distribuição Round-Robin */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="sync_alt" />
                  <h4>Distribuição Round-Robin</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={distributionEnabled}
                    onChange={(e) => setDistributionEnabled(e.target.checked)}
                    disabled={loadingDistribution}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {distributionEnabled
                      ? '✅ Distribuição ativa'
                      : '❌ Distribuição inativa'}
                  </span>
                </label>
                <small className="toggle-description">
                  {distributionEnabled
                    ? 'Leads serão distribuídos automaticamente entre atendentes em rodízio'
                    : 'Nenhuma distribuição automática será feita'}
                </small>
              </div>

              {distributionEnabled && (
                <div className="distribution-config">
                  <p className="distribution-info">
                    Selecione as listas dos atendentes na ordem do rodízio:
                  </p>

                  <div className="form-group">
                    <label>Adicionar lista de atendente:</label>
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddDistributionList(e.target.value);
                          e.target.value = '';
                        }
                      }}
                    >
                      <option value="">Selecione uma lista...</option>
                      {projectLists
                        .filter(l => l.id !== list?.id && !distributionTargetLists.find(t => t.list_id === l.id))
                        .map(l => (
                          <option key={l.id} value={l.id}>{l.name}</option>
                        ))}
                    </select>
                  </div>

                  {distributionTargetLists.length > 0 && (
                    <div className="target-lists-container">
                      <div className="target-lists-label">
                        Ordens de distribuição ({distributionTargetLists.length}):
                      </div>
                      <div className="target-lists-list">
                        {distributionTargetLists.map((item, index) => (
                          <div key={item.list_id} className="target-list-item">
                            <div className="list-order">#{index + 1}</div>
                            <div className="list-name">{item.name}</div>
                            <div className="list-actions">
                              <button
                                onClick={() => handleMoveDistributionList(index, 'up')}
                                disabled={index === 0}
                                title="Mover para cima"
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => handleMoveDistributionList(index, 'down')}
                                disabled={index === distributionTargetLists.length - 1}
                                title="Mover para baixo"
                              >
                                ↓
                              </button>
                              <button
                                onClick={() => handleRemoveDistributionList(index)}
                                className="btn-delete"
                                title="Remover"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {distributionConfig && (
                        <div className="distribution-stats">
                          <small>
                            Total distribuído: {distributionConfig.total_distributed || 0} leads
                            {distributionConfig.last_assigned_index >= 0 && (
                              <span> | Último: #{distributionConfig.last_assigned_index + 1}</span>
                            )}
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="info-box">
                    <AppIcon name="info" />
                    <p>
                      O primeiro lead será enviado para a lista #1, o segundo para #2,
                      e assim por diante. Quando chegar ao final, volta para o início (round-robin).
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* ✅ NOVO: Seção de Encaminhar para Projeto */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="forward" />
                  <h4>Encaminhar para Projeto</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={forwardEnabled}
                    onChange={(e) => setForwardEnabled(e.target.checked)}
                    disabled={loadingForward}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {forwardEnabled
                      ? '✅ Encaminhamento ativo'
                      : '❌ Encaminhamento inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {forwardEnabled
                    ? 'Cards serão automaticamente encaminhados para outro projeto'
                    : 'Nenhum encaminhamento automático será feito'}
                </small>
              </div>

              {forwardEnabled && (
                <div className="forward-config">
                  <p className="forward-info">
                    Selecione o projeto e lista de destino:
                  </p>

                  <div className="form-group">
                    <label>Projeto de destino:</label>
                    <select
                      value={forwardTargetProjectId}
                      onChange={(e) => handleProjectChange(e.target.value)}
                      disabled={loadingForward}
                    >
                      <option value="">Selecione um projeto...</option>
                      {availableProjects
                        .filter(p => p.id !== projectId)
                        .map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name} {p.project_type ? `(${p.project_type})` : ''}
                          </option>
                        ))}
                    </select>
                  </div>

                  {forwardTargetProjectId && (
                    <div className="form-group">
                      <label>Lista de destino:</label>
                      <select
                        value={forwardTargetListId}
                        onChange={(e) => setForwardTargetListId(e.target.value)}
                        disabled={loadingForward}
                      >
                        <option value="">Selecione uma lista...</option>
                        {availableLists.map(l => (
                          <option key={l.id} value={l.id}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Modo de encaminhamento:</label>
                    <div className="forward-mode-options">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="forwardMode"
                          value="immediate"
                          checked={forwardMode === 'immediate'}
                          onChange={() => setForwardMode('immediate')}
                          disabled={loadingForward}
                        />
                        <span>Enviar imediatamente</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="forwardMode"
                          value="scheduled"
                          checked={forwardMode === 'scheduled'}
                          onChange={() => setForwardMode('scheduled')}
                          disabled={loadingForward}
                        />
                        <span>Agendar envio</span>
                      </label>
                    </div>
                  </div>

                  {forwardMode === 'scheduled' && (
                    <div className="form-group">
                      <label>Agendar após (horas):</label>
                      <input
                        type="number"
                        min="1"
                        max="168"
                        value={forwardDelayHours}
                        onChange={(e) => setForwardDelayHours(parseInt(e.target.value) || 1)}
                        disabled={loadingForward}
                        className="delay-input"
                      />
                      <small>Máximo: 168 horas (7 dias)</small>
                    </div>
                  )}

                  {forwardTargetProjectId && forwardTargetListId && (
                    <div className="forward-preview">
                      <div className="preview-label">Resumo da configuração:</div>
                      <div className="preview-box">
                        <p>
                          <strong>Destino:</strong>{' '}
                          {availableProjects.find(p => p.id === forwardTargetProjectId)?.name} →{' '}
                          {availableLists.find(l => l.id === forwardTargetListId)?.name}
                        </p>
                        <p>
                          <strong>Envio:</strong>{' '}
                          {forwardMode === 'immediate'
                            ? 'Imediato (quando o card entrar na lista)'
                            : `Agendado (após ${forwardDelayHours} horas)`}
                        </p>
                        {forwardConfig && (
                          <p>
                            <strong>Total encaminhado:</strong>{' '}
                            {forwardConfig.total_forwarded || 0} cards
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="info-box">
                    <AppIcon name="info" />
                    <p>
                      Quando um card entrar nesta lista, ele será automaticamente movido para o projeto e lista selecionados.
                      Útil para fluxos de qualificação entre projetos CRM diferentes.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* ✅ Seção de Follow-up Automático */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="schedule" />
                  <h4>Follow-up Automático</h4>
                </div>
              </div>

              {/* ✅ Toggle de Habilitação */}
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={followUpEnabled}
                    onChange={(e) => setFollowUpEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {followUpEnabled 
                      ? '✅ Follow-up ativo' 
                      : '❌ Follow-up inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {followUpEnabled 
                    ? 'Leads serão movidos automaticamente conforme as regras configuradas'
                    : 'Nenhum lead será movido automaticamente'}
                </small>
              </div>

              {/* Lista de Regras Existentes */}
              {followUpRules.length > 0 && (
                <div className="followup-rules-container">
                  <div className="followup-rules-label">Regras Configuradas ({followUpRules.length}):</div>
                  <div className="followup-rules-list">
                    {followUpRules.map((rule, index) => (
                      <div key={rule.id} className="followup-rule-item">
                        <div className="rule-info">
                          <div className="rule-number">Follow-up {index + 1}</div>
                          <div className="rule-details">
                            <span className="rule-arrow">→</span>
                            <span className="rule-target">{rule.target_list_name}</span>
                            <span className="rule-delay">após {rule.delay_hours}h</span>
                          </div>
                        </div>
                        <div className="rule-actions">
                          <button
                            className={`btn-toggle-rule ${rule.active ? 'active' : 'inactive'}`}
                            onClick={() => handleToggleFollowUpRule(rule)}
                            disabled={loadingRules}
                            title={rule.active ? 'Desativar' : 'Ativar'}
                          >
                            <AppIcon name={rule.active ? 'check_circle' : 'radio_button_unchecked'} />
                          </button>
                          <button
                            className="btn-delete-rule"
                            onClick={() => handleDeleteFollowUpRule(rule.id)}
                            disabled={loadingRules}
                            title="Deletar"
                          >
                            <AppIcon name="delete" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulário para Criar Nova Regra */}
              {!showFollowUpForm ? (
                <button
                  className="btn-add-followup-rule"
                  onClick={() => setShowFollowUpForm(true)}
                  disabled={loadingRules}
                >
                  <AppIcon name="add" />
                  Adicionar Regra de Follow-up
                </button>
              ) : (
                <div className="followup-form">
                  <div className="form-group">
                    <label>Quantas horas para encaminhar?</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Ex: 1, 2, 24, 48"
                      value={newRule.delayHours}
                      onChange={(e) => setNewRule({ ...newRule, delayHours: e.target.value })}
                      disabled={loadingRules}
                      className="delay-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Encaminhar para</label>
                    <select
                      value={newRule.targetListId}
                      onChange={(e) => setNewRule({ ...newRule, targetListId: e.target.value })}
                      disabled={loadingRules}
                    >
                      <option value="">Selecione uma lista de follow-up...</option>
                      {projectLists
                        .filter(l => 
                          l.id !== list.id && 
                          (l.name.toLowerCase().includes('follow-up') || 
                           l.name.toLowerCase().includes('followup'))
                        )
                        .map(l => (
                          <option key={l.id} value={l.id}>
                            {l.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-actions">
                    <button
                      className="btn-cancel-form"
                      onClick={() => setShowFollowUpForm(false)}
                      disabled={loadingRules}
                    >
                      Cancelar
                    </button>
                    <button
                      className="btn-create-rule"
                      onClick={handleCreateFollowUpRule}
                      disabled={loadingRules || !newRule.targetListId || !newRule.delayHours}
                    >
                      {loadingRules ? 'Salvando...' : 'Salvar Configuração'}
                    </button>
                  </div>
                </div>
              )}

              <small className="followup-description">
                Quando habilitado, leads que permanecerem nesta lista por mais de N horas serão automaticamente movidos para a lista de destino.
              </small>
            </div>
          </div>

          {/* Sidebar */}
          <div className="crm-modal-sidebar">
            <h4>Informações</h4>
            
            {/* ✅ Informações da conexão */}
            {connection && (
              <div className="info-box-sidebar">
                <div className="info-item">
                  <AppIcon name="phone" />
                  <div>
                    <strong>Conexão</strong>
                    <p>{connection.name}</p>
                    <p style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{connection.number}</p>
                  </div>
                </div>
              </div>
            )}

            {!isNewList && (
              <>
                <div className="info-box-sidebar">
                  <div className="info-item">
                    <AppIcon name="credit_card" />
                    <div>
                      <strong>Cards</strong>
                      <p>{list?.cards?.length || 0} itens</p>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <AppIcon name="schedule" />
                    <div>
                      <strong>Criada em</strong>
                      <p>{list?.created_at ? new Date(list.created_at).toLocaleDateString('pt-BR') : 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="crm-modal-footer">
          <button className="btn-cancel-crm" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-save-crm" 
            onClick={handleSave}
            disabled={loading}
          >
            <AppIcon name="check" />
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}
